import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { getTransferData, updateTransfer } from '../../services/Transfer/transferService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import { usePermissions } from '../../hooks/usePermissions';
import DateInput from '../Utils/DateInput';
import HarvestInput from '../Utils/HarvestInput';
import PersonInput from '../Utils/PersonInput';
import FarmInput from '../Utils/FarmInput';
import SiloInput from '../Utils/SiloInput';
import ProductInput from '../Utils/ProductInput';
import SelectInput from '../Utils/SelectInput';
import WeightInput from '../Utils/WeightInput';
import InputField from '../Utils/InputField';
import { convertDateToISO } from '../Utils/utils';
import { Operations } from '../Enum/OperationsEnum';

const EditTransferModal = ({ show, handleClose, transfer, refreshTransfers }) => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('transferDelete');
  const { register, handleSubmit, formState: { errors }, reset, setValue, control, watch } = useForm({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTransferData = async () => {
      try {
        if (transfer?.id) {
          const data = await getTransferData(transfer.id);
          setValue('oldId', data?.oldId);
          setValue('transferDate', data?.transferDate);
          setValue('harvestId',{ id: data.harvest?.id, description: data.harvest?.description });
          setValue('originPersonId',{ id: data.originPerson?.id, name: data.originPerson?.oldId + ' - ' + data.originPerson?.name });
          setValue('destinyPersonId', data?.destinyPerson ? { id: data.destinyPerson?.id, name: data.destinyPerson?.oldId + ' - ' + data.destinyPerson?.name }: null);
          setValue('siloId',{ id: data.silo?.id, name: data.silo?.name });
          setValue('farmId',{ id: data.farm?.id, name: data.farm?.name });
          setValue('productId',{ id: data.product?.id, description: data.product?.description });
          setValue('type', data?.type);
          setValue('quantity', data?.quantity);
          setValue('description', data?.description);
          setValue('active', data?.active);
        }
      } catch (error) {
        setErrorMessage(error.message || 'Erro ao buscar dados da transferência.');
        setShowErrorModal(true);
      }
    };

    if (show && transfer) {
      fetchTransferData();
    } else if (!show) {
      reset();
    }
  }, [show, transfer, setValue, reset]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        harvestId: data?.harvestId ? data.harvestId.id : null,
        originPersonId: data.originPersonId.id,
        destinyPersonId: data?.destinyPersonId ? data.destinyPersonId.id: null,
        siloId: data?.siloId ? data.siloId.id : null,
        farmId: data?.farmId ? data.farmId.id : null,
        productId: data.productId.id,
        transferDate: data.transferDate ? convertDateToISO(data.transferDate) : null,
      }

      await updateTransfer(transfer.id, payload);
      setSuccessMessage('Transferência atualizada com sucesso!');
      setShowSuccessModal(true);
      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao atualizar transferência.');
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshTransfers();
    handleClose();
  };

  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show, reset]);

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Transferência <b>#{watch("oldId")}</b></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={3}>
                <Controller
                  name="transferDate"
                  control={control}
                  rules={{ required: 'Data é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <DateInput
                      isEditing = { true }
                      label="Data"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="__/__/____"
                    />
                  )}
                />
              </Col>
              <Col md={9}>
                <Controller
                  name="harvestId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <HarvestInput
                      value={field.value}
                      onChange={(selectedHarvest) => setValue('harvestId', selectedHarvest)}
                      label="Safra"
                      createdNew={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="originPersonId"
                  control={control}
                  rules={{ required: 'Origem é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      onChange={(selectedLot) => setValue('originPersonId', selectedLot)}
                      label="Origem"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                      type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
              <Controller
                  name="destinyPersonId"
                  control={control}
                  rules={{
                    required: 'Destino é obrigatório',
                  }}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) =>  setValue('destinyPersonId', selected)}
                      onCreated={(created) => {
                        setValue('destinyPersonId', { id: created.id, name: created.name });
                      }}
                      label="Destino"
                      isInvalid={!!fieldState.error}
                      type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="siloId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <SiloInput
                      value={field.value}
                      onChange={(selectedSilo) => setValue('siloId', selectedSilo)}
                      label="Silo"
                      createdNew={true}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="farmId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FarmInput
                      value={field.value}
                      onChange={(selectedFarm) => setValue('farmId', selectedFarm)}
                      label="Fazenda"
                      createdNew={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="productId"
                  control={control}
                  rules={{ required: 'Cultura é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <ProductInput
                      value={field.value}
                      onChange={(selectedProduct) => setValue('productId', selectedProduct)}
                      label="Cultura"
                      isInvalid={!!fieldState.error}
                      createdNew={true}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'Operação é obrigatória' }}
                  render={({ field }) => (
                    <SelectInput
                      data={Operations}
                      value={field.value}
                      onChange={(selected) => field.onChange(selected)}
                      label="Operação"
                      isInvalid={!!errors.type}
                      errorMessage={errors.type?.message}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
            <Col md={3}>
                <Controller
                  name="quantity"
                  control={control}
                  rules={{ required: 'Quantidade é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Quantidade"
                      isInvalid={!!fieldState.error}
                      placeholder="0.000"
                      errorMessage={errors.operation?.message}
                    />
                  )}
                />
              </Col>
              <Col md={9}>
                <InputField
                  label="Observações"
                  name="description"
                  placeholder="Observações"
                  register={register}
                  errors={errors}
                />
              </Col>   
            </Row>
            <Row>
              <Col md={12}>
                <Form.Label className="center">Status</Form.Label>
                <Controller
                  name="active"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Form.Check
                      type="checkbox"
                      label="Confirmado"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={!canDelete}
                    />
                  )}
                />
              </Col>   
            </Row>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit" disabled={isSubmitting}>
              Atualizar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default EditTransferModal;